import { cn, Typography } from "@suraasa/placebo-ui"
import { ListPromptHistory, ToolType } from "api/resources/aiTools/types"
import clsx from "clsx"
import TruncatedText from "components/TruncatedText"
import { getToolName } from "features/AItools/helper"
import {
  BookStack,
  EditPencil,
  Keyframes,
  ListSelect,
  Notes,
  Page,
  Palette,
  SoundLow,
  ViewGrid,
} from "iconoir-react"
import React from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import routes from "routes"
import { capitalizeFirstLetter, formatDate } from "utils/helpers"

export type PromptCardType = ListPromptHistory & {
  toolType: ToolType
  showType?: boolean
  isGenerator?: boolean
  useReplaceWhileNavigating?: boolean
  openHistorySideBar?: boolean
  toggleHistorySideBar?: (state: boolean) => void
} & Omit<
    React.DetailedHTMLProps<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      HTMLButtonElement
    >,
    "id"
  >

const YoutubeSvg = ({ className }: { className?: string }) => (
  <svg
    width="24px"
    height="24px"
    strokeWidth="1.5"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    color="currentColor"
    className={className}
  >
    <path
      d="M2 12.7075V11.2924C2 8.39705 2 6.94939 2.90549 6.01792C3.81099 5.08645 5.23656 5.04613 8.08769 4.96549C9.43873 4.92728 10.8188 4.8999 12 4.8999C13.1812 4.8999 14.5613 4.92728 15.9123 4.96549C18.7634 5.04613 20.189 5.08645 21.0945 6.01792C22 6.94939 22 8.39705 22 11.2924V12.7075C22 15.6028 22 17.0505 21.0945 17.9819C20.189 18.9134 18.7635 18.9537 15.9124 19.0344C14.5613 19.0726 13.1812 19.1 12 19.1C10.8188 19.1 9.43867 19.0726 8.0876 19.0344C5.23651 18.9537 3.81097 18.9134 2.90548 17.9819C2 17.0505 2 15.6028 2 12.7075Z"
      stroke="currentColor"
      strokeWidth="1.5"
    ></path>
    <path
      d="M14 12L10.5 14V10L14 12Z"
      fill="white"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
  </svg>
)

const getRoute = (type: ToolType): string => {
  switch (type) {
    case ToolType.lessonPlanPro:
    case ToolType.lessonPlan:
      return routes.aiTools.lessonPlan
    case ToolType.rubricsPro:
    case ToolType.rubrics:
      return routes.aiTools.rubrics
    case ToolType.quizPro:
    case ToolType.quiz:
      return routes.aiTools.quiz
    case ToolType.handout:
      return routes.aiTools.handout
    case ToolType.narration:
      return routes.aiTools.narration
    case ToolType.subjectiveAssessmentPro:
    case ToolType.subjectiveAssessment:
      return routes.aiTools.subjectiveAssessment
    case ToolType.assignmentPro:
    case ToolType.assignment:
      return routes.aiTools.assignment
    case ToolType.activityPlanner:
      return routes.aiTools.activityPlanner
    case ToolType.videoSummary:
      return routes.aiTools.videoSummary
    case ToolType.unitPlanner:
      return routes.aiTools.unitPlanner
    case ToolType.coverLetter:
      throw new Error(
        "This function should not be called with type=cover_letter"
      )
  }
}

const getIcon = (
  type: ToolType
): typeof Page | ((props: any) => JSX.Element) => {
  switch (type) {
    case ToolType.lessonPlan:
    case ToolType.lessonPlanPro:
      return Page
    case ToolType.rubrics:
    case ToolType.rubricsPro:
      return Keyframes
    case ToolType.quiz:
    case ToolType.quizPro:
      return ViewGrid
    case ToolType.handout:
      return Notes
    case ToolType.narration:
      return SoundLow
    case ToolType.subjectiveAssessment:
    case ToolType.subjectiveAssessmentPro:
      return ListSelect
    case ToolType.activityPlanner:
      return Palette
    case ToolType.assignmentPro:
    case ToolType.assignment:
      return EditPencil
    case ToolType.videoSummary:
      return YoutubeSvg
    case ToolType.unitPlanner:
      return BookStack
    case ToolType.coverLetter:
      throw new Error(
        "This function should not be called with type=cover_letter"
      )
  }
}

const getColorClassName = (
  type: ToolType
): {
  primary: string
  selected: string
  parent: string
  plus: string
} => {
  switch (type) {
    case ToolType.lessonPlan:
    case ToolType.lessonPlanPro:
      return {
        primary: "bg-decorative-one-50 text-decorative-one-100",
        selected: "text-decorative-one-100 bg-[#f5dbbd]",
        parent: "bg-decorative-one-50",
        plus: "bg-decorative-one-100",
      }
    case ToolType.rubrics:
    case ToolType.rubricsPro:
      return {
        primary: "bg-decorative-three-50 text-decorative-three-100",
        selected: "text-decorative-three-100 bg-[#c3e7ee]",
        plus: "bg-decorative-three-100",
        parent: "bg-decorative-three-50",
      }
    case ToolType.quiz:
    case ToolType.quizPro:
      return {
        primary: "bg-decorative-four-50 text-decorative-four-100",
        selected: "text-decorative-four-100 bg-[#dbe8c9]",
        plus: "bg-decorative-four-100",
        parent: "bg-decorative-four-50",
      }
    case ToolType.handout:
      return {
        primary: "bg-interactive-50 text-interactive-500",
        selected: "text-interactive-500 bg-interactive-200",
        plus: "bg-interactive-500",
        parent: "bg-interactive-50",
      }
    case ToolType.narration:
      return {
        primary: "bg-success-50 text-success-500",
        selected: "text-success-500 bg-success-200",
        plus: "bg-success-500",
        parent: "bg-success-50",
      }
    case ToolType.subjectiveAssessment:
    case ToolType.subjectiveAssessmentPro:
      return {
        primary: "bg-decorative-two-50 text-decorative-two-100",
        selected: "text-decorative-two-100 bg-[#d9cde5]",
        plus: "bg-decorative-two-100",
        parent: "bg-decorative-two-50",
      }
    case ToolType.activityPlanner:
      return {
        primary: "bg-[#FFF1EB] text-[#FEAE8D]",
        selected: "text-[#FFF1EB] bg-[#FEAE8D]",
        plus: "bg-[#FEAE8D]",
        parent: "bg-[#FFF1EB]",
      }
    case ToolType.unitPlanner:
      return {
        primary: "bg-[#FFF1F9] text-[#E478B5]",
        selected: "text-[#FFF1F9] bg-[#E478B5]",
        plus: "bg-[#E478B5]",
        parent: "bg-[#FFF1F9]",
      }
    case ToolType.videoSummary:
      return {
        primary: "bg-critical-50 [&>svg]:fill-critical-500 text-critical-500",
        selected:
          "bg-critical-500 [&>svg]:fill-critical-50 text-critical-50 [&>svg>path:nth-of-type(2)]:stroke-critical-500 [&>svg>path:nth-of-type(2)]:fill-critical-500",
        plus: "bg-critical-500",
        parent: "bg-critical-50",
      }

    case ToolType.assignment:
    case ToolType.assignmentPro:
      return {
        primary: "bg-warning-50 text-warning-500",
        selected: "text-warning-500 bg-warning-200",
        plus: "bg-warning-500",
        parent: "bg-warning-50",
      }
    case ToolType.coverLetter:
      throw new Error(
        "This function should not be called with type=cover_letter"
      )
  }
}

const HistoryItem = ({
  title,
  toolType,
  className,
  onClick,
  dateCreated,
  useReplaceWhileNavigating = false,
  showType,
  promptDataId,
  isGenerator,
  openHistorySideBar,
  toggleHistorySideBar,
}: PromptCardType) => {
  const navigate = useNavigate()

  const isPro = toolType.includes("_pro")

  const [searchParams] = useSearchParams()
  const currentPromptDataId = searchParams.get("id") || "-1"
  const isSelected = promptDataId === parseInt(currentPromptDataId)
  // const isSelected = contextPromptDataId
  //   ? promptDataId === contextPromptDataId
  //   : promptDataId === parseInt(currentPromptDataId)

  const Icon = getIcon(toolType)
  const colorClassName = getColorClassName(toolType)

  return (
    <button
      id={promptDataId ? promptDataId.toString() : undefined}
      className={cn(
        "my-2 flex w-full items-center gap-2 rounded-2xl text-start transition-colors duration-100 ease-linear",

        className,
        {
          "p-1 my-1": isGenerator,
          [colorClassName.parent]: isSelected,
        }
      )}
      onClick={e => {
        if (openHistorySideBar && toggleHistorySideBar)
          toggleHistorySideBar(false)
        navigate(
          {
            pathname: getRoute(toolType),
            search: `?id=${promptDataId}`,
          },
          { replace: useReplaceWhileNavigating }
        )
        if (onClick) onClick(e)
      }}
    >
      <div
        className={clsx(
          "relative grid size-8 shrink-0 place-items-center overflow-hidden rounded-2xl transition-colors duration-100 ease-linear",
          {
            [colorClassName.primary]: !isSelected,
            [colorClassName.selected]: isSelected,
          }
        )}
      >
        {
          // eslint-disable-next-line react-hooks/static-components
          Icon && <Icon />
        }
        {isPro && (
          <div
            className={clsx(
              "absolute -bottom-0.25 -right-2 grid h-2.75 w-[54px] rotate-[140deg] place-items-center fill-white",
              {
                [colorClassName.plus]: true,
              }
            )}
          >
            <PlusSvg className="rotate-[40deg] fill-inherit" />
          </div>
        )}
      </div>
      <div className="w-full">
        {title && (
          <TruncatedText maxLength={74} hideActionButton variant="subtitle2">
            {capitalizeFirstLetter(title.replaceAll("*", ""))}
          </TruncatedText>
        )}
        {dateCreated && (
          <Typography variant="smallBody" className="text-onSurface-500">
            {[
              showType ? getToolName(toolType) : "",
              formatDate(dateCreated, "MMM d, yyyy"),
            ]
              .filter(Boolean)
              .join(" • ")}
          </Typography>
        )}
      </div>
    </button>
  )
}
const PlusSvg = (props: React.SVGProps<any>) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.61913 0.779307C6.63302 0.781963 6.64505 0.785724 6.6562 0.791063C6.68951 0.807013 6.71727 0.832583 6.73589 0.86447C6.75705 0.900681 6.76098 0.948519 6.76885 1.04419L7.11586 5.264L7.11647 5.26405L6.76945 1.04431C6.76159 0.948636 6.75765 0.9008 6.7365 0.864589C6.71787 0.832702 6.69011 0.807132 6.65681 0.791182C6.6455 0.785765 6.63328 0.781969 6.61913 0.779307ZM11.4609 5.62486C11.4829 5.62924 11.5002 5.63509 11.5154 5.64397C11.5473 5.6626 11.5728 5.69035 11.5888 5.72366C11.6069 5.76148 11.6069 5.80948 11.6069 5.90548V6.45827C11.6069 6.55427 11.6069 6.60227 11.5888 6.64009C11.5728 6.6734 11.5473 6.70115 11.5154 6.71978C11.4792 6.74093 11.4313 6.74487 11.3357 6.75274L7.11586 7.09975L6.76885 11.3196C6.76098 11.4152 6.75705 11.4631 6.73589 11.4993C6.71727 11.5312 6.68951 11.5567 6.6562 11.5727C6.61838 11.5908 6.57038 11.5908 6.4744 11.5908H6.47438H5.92159H5.92158C5.85429 11.5908 5.81059 11.5908 5.77744 11.5846C5.81067 11.5909 5.8545 11.5909 5.9222 11.5909H6.47499C6.57099 11.5909 6.61899 11.5909 6.65681 11.5728C6.69011 11.5569 6.71787 11.5313 6.7365 11.4994C6.75765 11.4632 6.76159 11.4153 6.76945 11.3197L7.11647 7.09987L11.3363 6.75285C11.432 6.74499 11.4798 6.74105 11.516 6.7199C11.5479 6.70127 11.5735 6.67351 11.5894 6.64021C11.6075 6.60238 11.6075 6.55439 11.6075 6.45839V5.9056C11.6075 5.8096 11.6075 5.7616 11.5894 5.72378C11.5735 5.69047 11.5479 5.66271 11.516 5.64409C11.5007 5.63512 11.4832 5.62925 11.4609 5.62486ZM5.28012 7.09982L5.28011 7.09975L1.06031 6.75274C1.00518 6.7482 0.965937 6.74497 0.935648 6.73901C0.96602 6.74505 1.00542 6.74829 1.06091 6.75285L5.28012 7.09982ZM5.39796 1.01902L5.06648 5.04988L1.03562 5.38136C0.993344 5.38483 0.949403 5.38843 0.911778 5.39453C0.86946 5.40139 0.817217 5.41385 0.764639 5.44456C0.692851 5.48649 0.635285 5.54899 0.599375 5.62397C0.573075 5.67889 0.564943 5.73198 0.561575 5.77472C0.55858 5.8127 0.558588 5.85678 0.558594 5.89918V5.89922V6.46477V6.4648C0.558588 6.50721 0.55858 6.55128 0.561575 6.58927C0.564943 6.63201 0.573075 6.6851 0.599375 6.74001C0.635285 6.815 0.692851 6.87749 0.764639 6.91943C0.817217 6.95014 0.869461 6.96259 0.911778 6.96945C0.949403 6.97555 0.993344 6.97916 1.03562 6.98263L5.06648 7.31411L5.39796 11.345C5.40143 11.3872 5.40503 11.4312 5.41113 11.4688C5.41799 11.5111 5.43045 11.5634 5.46116 11.6159C5.5031 11.6877 5.56559 11.7453 5.64057 11.7812C5.69549 11.8075 5.74858 11.8156 5.79132 11.819C5.82932 11.822 5.87341 11.822 5.91583 11.822H6.48136C6.52378 11.822 6.56787 11.822 6.60587 11.819C6.64861 11.8156 6.7017 11.8075 6.75661 11.7812C6.8316 11.7453 6.89409 11.6877 6.93603 11.6159C6.96674 11.5634 6.9792 11.5111 6.98606 11.4688C6.99216 11.4312 6.99576 11.3872 6.99923 11.345L7.33071 7.31411L11.3616 6.98263L11.3616 6.98263C11.4039 6.97916 11.4478 6.97555 11.4854 6.96945C11.5277 6.96259 11.58 6.95014 11.6326 6.91943C11.7043 6.87749 11.7619 6.815 11.7978 6.74001C11.8241 6.6851 11.8322 6.63201 11.8356 6.58927C11.8386 6.55128 11.8386 6.50719 11.8386 6.46478V6.46476V5.89923V5.8992C11.8386 5.85679 11.8386 5.81271 11.8356 5.77472C11.8322 5.73198 11.8241 5.67889 11.7978 5.62397C11.7619 5.54899 11.7043 5.48649 11.6326 5.44456C11.58 5.41385 11.5277 5.40139 11.4854 5.39453C11.4478 5.38843 11.4038 5.38483 11.3616 5.38136L7.33071 5.04988L6.99923 1.01902C6.99576 0.976742 6.99216 0.932802 6.98606 0.895177C6.9792 0.852859 6.96674 0.800615 6.93603 0.748038C6.89409 0.67625 6.8316 0.618683 6.75661 0.582774C6.7017 0.556474 6.64861 0.548341 6.60587 0.544973C6.56788 0.541979 6.52381 0.541986 6.4814 0.541993H6.48137H5.91582H5.91578C5.87338 0.541986 5.82931 0.541979 5.79132 0.544973C5.74858 0.548341 5.69549 0.556474 5.64057 0.582774C5.56559 0.618683 5.5031 0.67625 5.46116 0.748038C5.43045 0.800615 5.41799 0.852859 5.41113 0.895177C5.40503 0.932802 5.40143 0.976743 5.39796 1.01902Z"
      fill="inherit"
    />
  </svg>
)

export default HistoryItem
